<template>
  <div
    class="app-sidebar"
    :class="{
      collapsed: collapsed,
      'mobile-open': mobileOpen,
      displayNone: modelValue === 'asset',
    }"
  >
    <!-- Toggle Button -->
    <div class="sidebar-toggle" @click="$emit('toggle-collapse')">
      <i
        class="pi"
        :class="collapsed ? 'pi-chevron-right' : 'pi-chevron-left'"
      ></i>
    </div>

    <div class="sidebar-nav">
      <div class="nav-section">
        <h4 class="section-title">Navigation</h4>
        <button
          class="sidebar-btn"
          :class="{ active: modelValue === 'dashboard' }"
          @click="$emit('update:modelValue', 'dashboard')"
          :title="collapsed ? 'Dashboard View' : ''"
        >
          <i class="pi pi-users"></i>
          <span v-if="!collapsed">Dashboard</span>
          <i class="pi pi-chevron-right nav-arrow" v-if="!collapsed"></i>
        </button>
        <!-- Provider Dropdown -->
        <button
          class="sidebar-btn sidebar-btn--parent"
          :class="{ active: isProviderActive }"
          @click="toggleProvider"
          :title="collapsed ? 'Provider' : ''"
        >
          <i class="pi pi-briefcase"></i>
          <span v-if="!collapsed">Provider View</span>
          <i
            class="pi pi-chevron-down nav-arrow"
            v-if="!collapsed"
            :class="{ 'nav-arrow--open': providerOpen }"
          ></i>
        </button>
        <div v-if="providerOpen && !collapsed" class="sidebar-subnav">
          <button
            class="sidebar-btn sidebar-sub-btn"
            :class="{ active: modelValue === 'provider-assets' }"
            @click="$emit('update:modelValue', 'provider-assets')"
          >
            <i class="pi pi-database"></i>
            <span>Provider Assets</span>
          </button>
          <button
            class="sidebar-btn sidebar-sub-btn"
            :class="{ active: modelValue === 'provider-policies' }"
            @click="$emit('update:modelValue', 'provider-policies')"
          >
            <i class="pi pi-shield"></i>
            <span>Provider Policies</span>
          </button>
          <button
            class="sidebar-btn sidebar-sub-btn"
            :class="{ active: modelValue === 'provider-offers' }"
            @click="$emit('update:modelValue', 'provider-offers')"
          >
            <i class="pi pi-tag"></i>
            <span>Provider Offers</span>
          </button>
          <button
            class="sidebar-btn sidebar-sub-btn"
            :class="{ active: modelValue === 'global-catalogs' }"
            @click="$emit('update:modelValue', 'global-catalogs')"
          >
            <i class="pi pi-globe"></i>
            <span>Global Catalogs</span>
          </button>
        </div>

        <!-- Consumer Dropdown -->
        <button
          class="sidebar-btn sidebar-btn--parent"
          :class="{ active: isConsumerActive }"
          @click="toggleConsumer"
          :title="collapsed ? 'Consumer' : ''"
        >
          <i class="pi pi-chart-line"></i>
          <span v-if="!collapsed">Consumer View</span>
          <i
            class="pi pi-chevron-down nav-arrow"
            v-if="!collapsed"
            :class="{ 'nav-arrow--open': consumerOpen }"
          ></i>
        </button>
        <div v-if="consumerOpen && !collapsed" class="sidebar-subnav">
          <button
            class="sidebar-btn sidebar-sub-btn"
            :class="{ active: modelValue === 'subscriber' }"
            @click="$emit('update:modelValue', 'subscriber')"
          >
            <i class="pi pi-users"></i>
            <span>Catalog Subscription</span>
          </button>
          <button
            class="sidebar-btn sidebar-sub-btn"
            :class="{ active: modelValue === 'consumer' }"
            @click="$emit('update:modelValue', 'consumer')"
          >
            <i class="pi pi-chart-line"></i>
            <span>Provider Catalog View</span>
          </button>
          <button
            class="sidebar-btn sidebar-sub-btn"
            :class="{ active: modelValue === 'acquired-assets' }"
            @click="$emit('update:modelValue', 'acquired-assets')"
          >
            <i class="pi pi-box"></i>
            <span>My Acquired Assets</span>
          </button>
        </div>

        <!-- <button
          class="sidebar-btn"
          :class="{ active: modelValue === 'global-catalog-ui' }"
          @click="$emit('update:modelValue', 'global-catalog-ui')"
          :title="collapsed ? 'Global Catalog UI' : ''"
        >
          <i class="pi pi-globe"></i>
          <span v-if="!collapsed">Global Catalog UI</span>
          <i class="pi pi-chevron-right nav-arrow" v-if="!collapsed"></i>
        </button> -->
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const props = defineProps({
  modelValue: { type: String },
  collapsed: { type: Boolean, default: false },
  mobileOpen: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "toggle-collapse"]);

const providerOpen = ref(false);
const consumerOpen = ref(false);

const isProviderActive = computed(() =>
  [
    "provider-assets",
    "provider-policies",
    "provider-offers",
    "global-catalogs",
  ].includes(props.modelValue)
);

const isConsumerActive = computed(() =>
  ["consumer", "acquired-assets", "subscriber"].includes(props.modelValue)
);

const toggleProvider = () => {
  if (props.collapsed) return;
  providerOpen.value = !providerOpen.value;
};

const toggleConsumer = () => {
  if (props.collapsed) return;
  consumerOpen.value = !consumerOpen.value;
};

watch(
  () => props.modelValue,
  (next) => {
    if (
      [
        "provider-assets",
        "provider-policies",
        "provider-offers",
        "global-catalogs",
      ].includes(next)
    ) {
      providerOpen.value = true;
    }
    if (["consumer", "acquired-assets", "subscriber"].includes(next)) {
      consumerOpen.value = true;
    }
  }
);
</script>

<style scoped>
/* Sidebar Base Styles */
.app-sidebar {
  position: fixed;
  top: 105px;
  left: 0;
  height: calc(100vh - 75.5px);
  width: 280px;
  background: linear-gradient(180deg, #108e8a 0%, #0a6d69 100%);
  color: white;
  z-index: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

/* Collapsed State */
.app-sidebar.collapsed {
  width: 80px;
}

.app-sidebar.displayNone {
  display: none;
}

/* Sidebar Toggle Button */
.sidebar-toggle {
  position: absolute;
  top: 1rem;
  right: 12px;
  width: 25px;
  height: 24px;
  background: white;
  border: 2px solid #108e8a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  transition: all 0.3s ease;
}

.sidebar-toggle:hover {
  background: #f0f0f0;
  transform: scale(1.1);
}

.sidebar-toggle i {
  font-size: 0.8rem;
  color: #108e8a;
}

.collapsed .sidebar-toggle {
  right: 20px;
}

/* Sidebar Navigation */
.sidebar-nav {
  flex: 1;
  padding: 3rem 0 1rem 0;
  transition: all 0.3s ease;
}

.collapsed .sidebar-nav {
  padding: 3rem 0 1rem 0;
}

.nav-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 1.25rem 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  opacity: 1;
  overflow: hidden;
}

.collapsed .section-title {
  opacity: 0;
  height: 0;
  margin: 0;
}

/* Sidebar Buttons */
.sidebar-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  border: none;
  background: transparent;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  color: rgba(255, 255, 255, 0.9);
  text-align: left;
  width: 100%;
  margin: 0.1rem 0;
  border-left: 3px solid transparent;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  margin-top: 10px;
}

.sidebar-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-left-color: rgba(255, 255, 255, 0.3);
}

.sidebar-btn.active {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border-left-color: white;
  font-weight: 500;
}

.sidebar-btn--parent {
  margin-top: 10px;
}

.nav-arrow--open {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

.sidebar-subnav {
  margin-left: 0.5rem;
  border-left: 1px solid rgba(255, 255, 255, 0.15);
  padding-left: 0.5rem;
  margin-top: 0.35rem;
}

.sidebar-sub-btn {
  padding: 0.65rem 1.25rem;
  font-size: 0.92rem;
  opacity: 0.95;
}

.sidebar-btn i:first-child {
  font-size: 1.1rem;
  min-width: 20px;
  opacity: 0.9;
  flex-shrink: 0;
}

.sidebar-btn span {
  flex: 1;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.3s ease;
  opacity: 1;
}

.collapsed .sidebar-btn span {
  opacity: 0;
  width: 0;
  flex: 0;
}

.nav-arrow {
  font-size: 0.8rem;
  opacity: 0.7;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.sidebar-btn:hover .nav-arrow {
  transform: translateX(3px);
  opacity: 1;
}

.collapsed .nav-arrow {
  opacity: 0;
  width: 0;
}

/* Tooltip for collapsed state */
.sidebar-btn::after {
  content: attr(title);
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  background: #333;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1;
  pointer-events: none;
}

.collapsed .sidebar-btn:hover::after {
  opacity: 1;
  visibility: visible;
  left: calc(100% + 10px);
}

/* Mobile First Responsive Design */
@media (max-width: 768px) {
  .app-sidebar {
    width: 280px;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  .app-sidebar.mobile-open {
    transform: translateX(0);
  }

  .app-sidebar.collapsed {
    width: 280px;
    transform: translateX(-100%);
  }

  .app-sidebar.collapsed.mobile-open {
    transform: translateX(0);
  }

  /* Hide toggle button on mobile since we'll use hamburger menu */
  .sidebar-toggle {
    display: none;
  }

  /* Adjust padding for mobile */
  .sidebar-nav {
    padding: 1rem 0;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .app-sidebar {
    width: 250px;
  }

  .app-sidebar.collapsed {
    width: 70px;
  }
}

@media (min-width: 1025px) {
  .app-sidebar {
    width: 280px;
  }

  .app-sidebar.collapsed {
    width: 70px;
  }
}

/* Ensure smooth transitions for all interactive elements */
.app-sidebar * {
  transition: all 0.3s ease;
}
</style>
